import { useEffect, useState } from "react";
import { api, fmtBRL, fmtDate, formatApiError } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Trash2, Loader2, CheckCircle2, HandCoins, X, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function AdminInvoices() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);

  // baixa manual
  const [markTarget, setMarkTarget] = useState(null);
  const [markNote, setMarkNote] = useState("");
  const [markingId, setMarkingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/invoices");
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const badge = (s) => {
    const map = {
      paid: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
      pending: "bg-amber-500/20 text-amber-400 border-amber-500/30",
      overdue: "bg-red-500/20 text-red-400 border-red-500/30",
      cancelled: "bg-zinc-500/20 text-zinc-400 border-zinc-500/30",
    };
    return <Badge className={`rounded-sm border ${map[s] || ""}`}>{s}</Badge>;
  };

  const remove = async (i) => {
    const isPaid = i.status === "paid";
    const msg = isPaid
      ? `Apagar fatura ${i.number} (PAGA) de ${fmtBRL(i.amount_brl)}?\n\nA fatura será removida do histórico. A VPS vinculada, se existir, será MANTIDA.\n\nEsta ação NÃO pode ser desfeita.`
      : `Apagar fatura ${i.number} (${i.status.toUpperCase()}) de ${fmtBRL(i.amount_brl)}?\n\nComo a fatura não está paga, a VPS vinculada (se houver) também será TERMINADA no portal FLIX e removida.\n\nEsta ação NÃO pode ser desfeita.`;
    if (!window.confirm(msg)) return;
    setDeletingId(i.invoice_id);
    try {
      const { data } = await api.delete(`/admin/invoices/${i.invoice_id}`);
      const vpsCount = (data.terminated_vps_ids || []).length;
      toast.success(
        vpsCount > 0
          ? `Fatura apagada. ${vpsCount} VPS vinculada(s) também foram terminadas.`
          : "Fatura apagada."
      );
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDeletingId(null);
    }
  };

  const openMark = (i) => { setMarkTarget(i); setMarkNote(""); };
  const closeMark = () => { if (!markingId) { setMarkTarget(null); setMarkNote(""); } };

  const confirmMark = async () => {
    if (!markTarget) return;
    setMarkingId(markTarget.invoice_id);
    try {
      await api.post(`/admin/invoices/${markTarget.invoice_id}/mark-paid`, {
        note: markNote.trim() || null,
      });
      toast.success(`Fatura ${markTarget.number} marcada como paga. VPS renovada/ativada.`);
      setMarkTarget(null);
      setMarkNote("");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setMarkingId(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-invoices-page">
      <div>
        <div className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500 mb-2">// admin · faturas</div>
        <h1 className="font-heading text-3xl font-bold tracking-tighter">Faturas</h1>
      </div>
      <div className="overflow-x-auto border border-white/10 bg-[#121214]">
        <table className="w-full min-w-[1050px] text-sm">
          <thead className="text-xs font-mono uppercase tracking-widest text-zinc-500">
            <tr className="border-b border-white/10">
              <th className="text-left px-5 py-3">Número</th>
              <th className="text-left px-5 py-3">Cliente</th>
              <th className="text-left px-5 py-3">VM da fatura</th>
              <th className="text-left px-5 py-3">Plano</th>
              <th className="text-left px-5 py-3">Criada</th>
              <th className="text-left px-5 py-3">Valor</th>
              <th className="text-left px-5 py-3">Status</th>
              <th className="text-right px-5 py-3">Ações</th>
            </tr>
          </thead>
          <tbody>
            {items.map((i) => {
              const canMark = i.payment_method !== "credit_card" &&
                (i.status === "pending" || i.status === "overdue" || i.status === "cancelled");
              const paidManual = i.status === "paid" && i.payment_method === "manual";
              return (
                <tr key={i.invoice_id} className="border-b border-white/5 hover:bg-white/5">
                  <td className="px-5 py-3 font-mono">{i.number}</td>
                  <td className="px-5 py-3 font-mono text-xs">{(i.user_id || "").slice(-8)}</td>
                  <td className="px-5 py-3">
                    {i.linked_vps?.length ? (
                      <div className="space-y-1.5">
                        {i.linked_vps.map((vps) => (
                          <div key={vps.vps_id || `${vps.hostname}-${vps.virtualizor_vps_id}`}>
                            <div className="font-medium text-zinc-200">{vps.hostname || "VPS sem nome"}</div>
                            <div className="font-mono text-xs text-zinc-500">VMID {vps.virtualizor_vps_id || "—"}</div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <span className="text-zinc-500">Sem VPS associada</span>
                    )}
                  </td>
                  <td className="px-5 py-3">{i.plan_name}</td>
                  <td className="px-5 py-3 font-mono text-xs">{fmtDate(i.created_at)}</td>
                  <td className="px-5 py-3 font-mono">{fmtBRL(i.amount_brl)}</td>
                  <td className="px-5 py-3">
                    {badge(i.status)}
                    {i.payment_review_required && (
                      <div className="mt-1 flex items-start gap-1 text-[10px] font-mono text-red-400 max-w-64"
                           data-testid={`admin-payment-review-${i.invoice_id}`}>
                        <AlertTriangle className="h-3 w-3 shrink-0" />
                        <span>Revisar pagamento: {i.payment_error || "Cobrança ou estorno pendente"}.
                          {i.duplicate_payment_ids?.length > 0 && ` IDs: ${i.duplicate_payment_ids.join(", ")}`}
                        </span>
                      </div>
                    )}
                    {paidManual && (
                      <div
                        className="mt-1 flex items-center gap-1 text-[10px] font-mono text-emerald-400/80"
                        data-testid={`admin-invoice-manual-seal-${i.invoice_id}`}
                      >
                        <CheckCircle2 className="h-3 w-3" />
                        Pago manualmente{i.paid_by_admin_email ? ` · ${i.paid_by_admin_email}` : ""}
                      </div>
                    )}
                  </td>
                  <td className="px-5 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      {canMark && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openMark(i)}
                          className="rounded-sm border-emerald-500/40 text-emerald-400 hover:bg-emerald-500 hover:text-white hover:border-emerald-500 h-8 px-3 gap-1.5"
                          data-testid={`admin-invoice-mark-paid-${i.invoice_id}`}
                        >
                          <HandCoins className="h-3.5 w-3.5" />
                          Dar baixa manual
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => remove(i)}
                        disabled={deletingId === i.invoice_id}
                        className="rounded-sm border-red-500/40 text-red-400 hover:bg-red-500 hover:text-white hover:border-red-500 h-8 px-3"
                        data-testid={`admin-invoice-delete-${i.invoice_id}`}
                      >
                        {deletingId === i.invoice_id
                          ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          : <Trash2 className="h-3.5 w-3.5" />}
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {items.length === 0 && !loading && (
              <tr><td colSpan={8} className="p-10 text-center text-zinc-500">Sem faturas.</td></tr>
            )}
            {loading && (
              <tr><td colSpan={8} className="p-10 text-center text-zinc-500 font-mono text-xs">Carregando…</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {markTarget && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          onClick={closeMark}
          data-testid="admin-mark-paid-modal"
        >
          <div
            className="w-full max-w-md border border-white/10 bg-[#121214] p-6 space-y-5"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between">
              <div>
                <div className="text-xs font-mono uppercase tracking-[0.2em] text-emerald-500 mb-1">// baixa manual</div>
                <h2 className="font-heading text-xl font-bold tracking-tight">Marcar como paga</h2>
              </div>
              <button
                onClick={closeMark}
                className="text-zinc-500 hover:text-white transition-colors"
                data-testid="admin-mark-paid-close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="text-sm text-zinc-400 space-y-1">
              <div>Fatura <span className="font-mono text-zinc-200">{markTarget.number}</span> · {markTarget.plan_name}</div>
              <div className="font-mono text-zinc-200">{fmtBRL(markTarget.amount_brl)}</div>
            </div>

            <div className="flex gap-3 border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-300">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
              <span>Isto <strong>renova/ativa a VPS do cliente</strong> (mesmo fluxo do pagamento automático) e, se houver PIX pendente, ele será cancelado no Mercado Pago.</span>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-mono uppercase tracking-widest text-zinc-500">Observação (opcional)</label>
              <textarea
                value={markNote}
                onChange={(e) => setMarkNote(e.target.value)}
                placeholder="Ex: PIX na chave X, comprovante enviado no WhatsApp…"
                rows={3}
                className="w-full resize-none rounded-sm border border-white/10 bg-black/40 px-3 py-2 text-sm text-zinc-200 outline-none focus:border-emerald-500/50"
                data-testid="admin-mark-paid-note"
              />
            </div>

            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={closeMark}
                disabled={!!markingId}
                className="rounded-sm border-white/10 h-9 px-4"
                data-testid="admin-mark-paid-cancel"
              >
                Cancelar
              </Button>
              <Button
                onClick={confirmMark}
                disabled={!!markingId}
                className="rounded-sm bg-emerald-500 text-white hover:bg-emerald-600 h-9 px-4 gap-1.5"
                data-testid="admin-mark-paid-confirm"
              >
                {markingId
                  ? <><Loader2 className="h-4 w-4 animate-spin" /> Processando…</>
                  : <><HandCoins className="h-4 w-4" /> Confirmar baixa</>}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
